import React, {useContext, useEffect, useState} from "react";
import {
	Button,
	CircularProgress,
	FormControlLabel,
	Switch,
	Typography
} from "@mui/material";
import AddCommentTwoToneIcon from "@mui/icons-material/AddCommentTwoTone";
import SendTwoToneIcon from "@mui/icons-material/SendTwoTone";
import {FormProvider, useForm} from "react-hook-form";
import clsx from "clsx";
import {AppContext, TicketContext} from "../../../../App";
import {getTicketHistory, searchTicket, updateTicket} from "../../../../services/TicketServices";
import {setOpenSnackbar} from "../../../../services/AppServices";
import CustomMultiText from "../../../widgets/inputs/CustomMultiText";
import FileInput from "../../carico/utils/FileInput";
import {TicketStyle} from "../../../../styles/pages/tickets/TicketStyle";

const noteRules = {
	maxLength: {
		value: 512,
		message: "Massimo 512 caratteri",
	},
};

const TicketHistoryComposer = ({ticketSelected}) => {
	const ticketClasses = TicketStyle();
	const ticketContext = useContext(TicketContext);
	const appContext = useContext(AppContext);
	const methods = useForm({mode: "onChange"});
	const [notifyUser, setNotifyUser] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (!submitting || ticketContext.state.loadingAddHistory) return;

		if (ticketContext.state.errorAddHistory) {
			setOpenSnackbar("error", ticketContext.state.errorAddHistory, appContext.dispatch);
			setSubmitting(false);
			return;
		}

		setOpenSnackbar("success", "Storico aggiornato", appContext.dispatch);
		methods.reset({note: "", allegati: []});
		setNotifyUser(false);
		searchTicket({id: ticketSelected.id}, ticketContext.dispatch);
		getTicketHistory({id: ticketSelected.id}, ticketContext.dispatch);
		setSubmitting(false);
	}, [
		submitting,
		ticketSelected,
		ticketContext.state.loadingAddHistory,
		ticketContext.state.errorAddHistory,
		ticketContext.dispatch,
		appContext.dispatch,
		methods,
	]);

	const onSubmit = (data) => {
		const note = String(data.note || "").trim();
		const allegati = data.allegati?.length ? data.allegati : null;

		if (!note && !allegati) {
			setOpenSnackbar("warning", "Inserisci una nota o allega un file", appContext.dispatch);
			return;
		}

		setSubmitting(true);
		updateTicket({
			...ticketSelected,
			...data,
			ticketId: ticketSelected.id,
			stato: ticketSelected.stato,
			priority: ticketSelected.priority,
			livello: ticketSelected.livello,
			allegati,
			note: [
				notifyUser ? "#MOSTRA_USER" : "",
				notifyUser ? "#IN_ATTESA_DI_RISPOSTA" : "",
				note || "#ALLEGATO_AGGIUNTO",
			].filter(Boolean).join(" "),
			rispostaUtente: false,
		}, ticketContext.dispatch, "NULL", notifyUser, "NULL", ticketSelected.idSoggetto);
	};

	return (
		<section className={ticketClasses.historyComposer}>
			<div className={ticketClasses.historyComposerHeader}>
				<div>
					<Typography component="h3" className={ticketClasses.ticketDetailOptionTitle}>
						Aggiungi allo storico
					</Typography>
					<Typography className={ticketClasses.ticketDetailOptionCopy}>
						Nota operativa e allegato vengono salvati come nuova voce del ticket.
					</Typography>
				</div>
				<FormControlLabel
					className={ticketClasses.ticketDetailSwitch}
					control={<Switch checked={notifyUser} onChange={() => setNotifyUser((value) => !value)} name="notifyUser"/>}
					label={notifyUser ? "Visibile e notificata" : "Solo interno"}
				/>
			</div>
			<FormProvider {...methods}>
				<form className={ticketClasses.historyComposerForm} onSubmit={methods.handleSubmit(onSubmit)}>
					<CustomMultiText
						inputRef={methods.register(noteRules)}
						name="note"
						label="Nota"
						autoFocus={false}
						error={methods.errors.note}
						inputProps={{maxLength: 512}}
						rows={3}
					/>
					<FileInput name="allegati" multiple={true} accept="*" compact={true}/>
					<div className={ticketClasses.historyComposerActions}>
						<Button
							className={clsx(ticketClasses.ticketDetailActionButton, notifyUser ? ticketClasses.ticketDetailActionPrimary : ticketClasses.ticketDetailActionSuccess)}
							disabled={submitting}
							disableElevation
							endIcon={submitting ? <CircularProgress size={16}/> : notifyUser ? <SendTwoToneIcon/> : <AddCommentTwoToneIcon/>}
							size="small"
							type="submit"
							variant="contained"
						>
							<b>{notifyUser ? "Aggiungi e invia" : "Aggiungi nota"}</b>
						</Button>
					</div>
				</form>
			</FormProvider>
		</section>
	);
};

export default React.memo(TicketHistoryComposer);
